import { useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/useToast';
import {
  useCampaignNegativeKeywordsQuery,
  useAddCampaignNegativeKeywordsMutation,
  useDeleteCampaignNegativeKeywordMutation,
} from '@/lib/query-options';
import type { NegativeKeywordMatchType } from '@/lib/apis';
import { Trash2, Ban, Plus, Send } from 'lucide-react';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { DataTable } from '@/components/glitch-ui/data-table';
import { IconButton } from '@/components/glitch-ui/icon-button';
import { ApplyListsToCampaignModal } from '@/components/campaign/settings/apply-lists-to-campaign-modal';
import { MatchTypeSelect } from '@/components/campaign/ads/match-type-select';

interface CampaignNegativeKeywordsViewProps {
  campaignId: number;
  disabled?: boolean;
}

// A unified row: the campaign's own negatives plus negatives inherited from applied shared lists.
// Inherited rows are read-only (managed on the account list); only own rows carry an id + delete.
interface NegativeRow {
  id: number | null;
  keyword: string;
  match_type: string;
  is_paused?: boolean;
  sourceLabel: string;
  sourceType: 'own' | 'shared_list';
  deletable: boolean;
}

const MATCH_TYPES: NegativeKeywordMatchType[] = ['EXACT', 'PHRASE', 'BROAD'];

const formatMatchType = (matchType: string): string => {
  return matchType.charAt(0) + matchType.slice(1).toLowerCase();
};

const getMatchTypePillStyle = (matchType: string) => {
  switch (matchType) {
    case 'EXACT':
      return 'bg-purple-50 text-purple-600';
    case 'PHRASE':
      return 'bg-blue-50 text-blue-600';
    case 'BROAD':
      return 'bg-gray-100 text-gray-600';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

// Shared-list rows get the cobalt info pill so inherited negatives stand out from the level's own.
const getSourcePillStyle = (sourceType: NegativeRow['sourceType']) =>
  sourceType === 'shared_list' ? 'bg-[#EDE9FE] text-[#290BD9]' : 'bg-gray-100 text-gray-700';

export const CampaignNegativeKeywordsView: React.FC<CampaignNegativeKeywordsViewProps> = ({
  campaignId,
  disabled = false,
}) => {
  const { toast } = useToast();
  const { data: keywordsData, isLoading } = useCampaignNegativeKeywordsQuery(campaignId, true);
  const addMutation = useAddCampaignNegativeKeywordsMutation(campaignId);
  const deleteMutation = useDeleteCampaignNegativeKeywordMutation(campaignId);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isApplyListsOpen, setIsApplyListsOpen] = useState(false);
  const [bulkKeywords, setBulkKeywords] = useState('');
  const [matchType, setMatchType] = useState<NegativeKeywordMatchType>('PHRASE');
  const [deleteTarget, setDeleteTarget] = useState<NegativeRow | null>(null);

  // Merge the campaign's own negatives (deletable) with inherited shared-list negatives (read-only).
  const rows: NegativeRow[] = [
    ...(keywordsData?.negative_keywords || []).map((nk): NegativeRow => ({
      id: nk.id,
      keyword: nk.keyword,
      match_type: nk.match_type,
      is_paused: nk.is_paused,
      sourceLabel: 'Campaign',
      sourceType: 'own',
      deletable: true,
    })),
    ...(keywordsData?.inherited_negatives || []).map((n): NegativeRow => ({
      id: null,
      keyword: n.keyword,
      match_type: n.match_type,
      sourceLabel: n.source_label,
      sourceType: 'shared_list',
      deletable: false,
    })),
  ];

  const isMutating = deleteMutation.isPending || addMutation.isPending;

  // Campaign negatives have no update endpoint, so an inline match-type change retires the old
  // criterion and recreates it with the new type (delete + add). Negatives carry no performance
  // history, so nothing is lost. Mirrors the ad-level destructive-change behavior (#5556 / #5555).
  const handleMatchTypeChange = async (row: NegativeRow, newMatchType: string) => {
    if (row.id == null || row.match_type.toUpperCase() === newMatchType) return;
    // Skip if the same keyword already exists with the target match type (mirrors the add dedup).
    const duplicate = rows.some(
      (r) =>
        r.sourceType === 'own' &&
        r.id !== row.id &&
        r.keyword.toLowerCase() === row.keyword.toLowerCase() &&
        r.match_type.toUpperCase() === newMatchType
    );
    if (duplicate) {
      toast({ description: `"${row.keyword}" already exists with match type ${newMatchType.toLowerCase()}.`, variant: 'destructive' });
      return;
    }
    try {
      // Create the new-match-type criterion BEFORE removing the old one, so a failed add leaves the
      // original negative keyword intact. Delete-then-add would permanently lose the keyword if the add
      // failed. Worst case here is a transient duplicate (old + new) if the delete then fails, which is
      // visible and recoverable rather than silent data loss.
      await addMutation.mutateAsync({ keywords: [{ keyword: row.keyword, match_type: newMatchType as NegativeKeywordMatchType }] });
      await deleteMutation.mutateAsync(row.id);
    } catch (err) {
      toast({ description: err instanceof Error ? err.message : 'Failed to change match type', variant: 'destructive' });
    }
  };

  const columns: ColumnDef<NegativeRow>[] = [
    {
      accessorKey: 'keyword',
      header: 'Keyword',
      sortingFn: 'alphanumeric',
      cell: ({ row }) => (
        <div className='flex items-center gap-3'>
          <Ban className={`h-4 w-4 ${row.original.is_paused ? 'text-gray-300' : 'text-gray-500'}`} />
          <span className={`text-sm font-medium ${row.original.is_paused ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
            {row.original.keyword}
          </span>
          {row.original.is_paused && (
            <span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500'>
              Paused
            </span>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'match_type',
      header: 'Match Type',
      sortingFn: 'alphanumeric',
      cell: ({ row }) => {
        // The campaign's own (non-paused) negatives get the inline editable pill (#5556); inherited
        // shared-list rows and paused rows stay as static colored pills.
        const editable = !disabled && row.original.deletable && !row.original.is_paused && row.original.id != null;
        if (editable) {
          return (
            <MatchTypeSelect
              matchType={row.original.match_type}
              onChange={(mt) => handleMatchTypeChange(row.original, mt)}
              pillClasses={getMatchTypePillStyle}
              matchTypes={MATCH_TYPES}
              disabled={isMutating}
            />
          );
        }
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${row.original.is_paused ? 'bg-gray-50 text-gray-400' : getMatchTypePillStyle(row.original.match_type)}`}>
            {formatMatchType(row.original.match_type)}
          </span>
        );
      },
    },
    {
      accessorKey: 'sourceLabel',
      header: 'Source',
      sortingFn: 'alphanumeric',
      cell: ({ row }) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSourcePillStyle(row.original.sourceType)}`}>
          {row.original.sourceLabel}
        </span>
      ),
    },
    {
      id: 'actions',
      enableSorting: false,
      header: () => null,
      cell: ({ row }) => (
        <div className='flex justify-end'>
          {row.original.deletable && !row.original.is_paused && (
            <IconButton
              variant='destructive'
              aria-label='Delete'
              onClick={() => setDeleteTarget(row.original)}
              disabled={disabled}
            >
              <Trash2 className='h-4 w-4' />
            </IconButton>
          )}
        </div>
      ),
    },
  ];

  const resetForm = () => {
    setBulkKeywords('');
    setMatchType('PHRASE');
    setIsModalOpen(false);
  };

  const openAddModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!bulkKeywords.trim()) {
      toast({
        description: 'Please enter at least one keyword',
        variant: 'destructive',
      });
      return;
    }

    const keywordLines = bulkKeywords
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    if (keywordLines.length === 0) {
      toast({
        description: 'No valid keywords found',
        variant: 'destructive',
      });
      return;
    }

    try {
      await addMutation.mutateAsync({
        keywords: keywordLines.map((keyword) => ({
          keyword,
          match_type: matchType,
        })),
      });
      toast({ description: `Added ${keywordLines.length} negative keyword(s)` });
      resetForm();
    } catch (err) {
      toast({
        description: err instanceof Error ? err.message : 'Failed to add negative keywords',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
    } catch (err) {
      toast({
        description: err instanceof Error ? err.message : 'Failed to delete negative keyword',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div>
        <h2 className='text-xl font-semibold text-gray-900'>Campaign Negative Keywords</h2>
        <p className='text-sm text-gray-600 mt-1'>Loading negative keywords...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className='flex items-center justify-between mb-4'>
        <div>
          <h2 className='text-xl font-semibold text-gray-900'>Campaign Negative Keywords</h2>
          <p className='text-sm text-gray-600 mt-1'>Exclude search terms from the entire campaign.</p>
        </div>
        <div className='flex items-center gap-2'>
          <Button
            variant='outline'
            className='text-gray-900'
            onClick={() => setIsApplyListsOpen(true)}
            disabled={disabled}
          >
            <Send className='h-4 w-4 mr-2' />
            Apply List
          </Button>
          <Button
            variant='outline'
            className='text-gray-900'
            onClick={openAddModal}
            disabled={disabled}
          >
            <Plus className='h-4 w-4 mr-2' />
            Add Negatives
          </Button>
        </div>
      </div>

      {/* Table */}
      <DataTable
        data={rows}
        columns={columns}
        variant='flush'
        pageSize={999}
        initialSorting={[{ id: 'keyword', desc: false }]}
        emptyIcon={Ban}
        emptyTitle='No Negative Keywords'
        emptyDescription='Add keywords to prevent your ads from showing for specific search terms.'
      />

      {/* Add Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className='sm:max-w-[500px]' aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Add Negative Keywords</DialogTitle>
          </DialogHeader>

          <div className='py-4 space-y-4'>
            <div className='space-y-2'>
              <Label>Keywords (one per line)</Label>
              <Textarea
                value={bulkKeywords}
                onChange={(e) => setBulkKeywords(e.target.value)}
                placeholder={'Enter keywords, one per line\nfree\ndownload\ncheap'}
                className='min-h-[150px] text-gray-900'
                disabled={disabled || addMutation.isPending}
              />
            </div>
            <div className='space-y-2'>
              <Label>Match Type</Label>
              <Select
                value={matchType}
                onValueChange={(value) => setMatchType(value as NegativeKeywordMatchType)}
                disabled={disabled || addMutation.isPending}
              >
                <SelectTrigger className='w-[200px]'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MATCH_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {formatMatchType(type)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className='text-xs text-gray-500'>Exact matches the keyword precisely. Phrase matches the keyword in order. Broad matches variations.</p>
            </div>
          </div>

          <div className='flex justify-end gap-2'>
            <Button variant='outline' onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={disabled || addMutation.isPending}
              className='bg-[#290BD9] hover:bg-[#2309C5]'
            >
              {addMutation.isPending ? 'Adding...' : 'Add'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <ConformationModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title='Delete Negative Keyword'
        description={`Are you sure you want to delete "${deleteTarget?.keyword}"? This action cannot be undone.`}
        confirmText='Delete'
        cancelText='Cancel'
        isLoading={deleteMutation.isPending}
      />

      {/* Apply shared negative lists to this campaign */}
      <ApplyListsToCampaignModal
        isOpen={isApplyListsOpen}
        onClose={() => setIsApplyListsOpen(false)}
        campaignId={campaignId}
      />
    </div>
  );
};
